import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { useData } from "../context/Context";
import { UpdateStatus } from "../services/Service";
import { FiExternalLink } from "react-icons/fi";
import { BASE_URL_FLASK } from "../services/serviceUrls";
import ThreeDEditor from "./ThreeDEditor.jsx";
import { useEffect, useState, useRef, Suspense } from "react";
import { Canvas, useLoader, useFrame } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import * as THREE from "three";
import toast from "react-hot-toast";

function TrellisModelMesh({ modelSource }) {
  const modelRef = useRef(null);
  const gltf = useLoader(GLTFLoader, modelSource);

  useEffect(() => {
    if (gltf.scene) {
      const box = new THREE.Box3().setFromObject(gltf.scene);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());

      gltf.scene.position.x -= center.x;
      gltf.scene.position.y -= center.y;
      gltf.scene.position.z -= center.z;

      const maxDimension = Math.max(size.x, size.y, size.z);
      const scale = 2 / maxDimension;
      gltf.scene.scale.set(scale, scale, scale);
    }
  }, [gltf]);

  useFrame(() => {
    if (modelRef.current) {
      modelRef.current.rotation.y += 0.005;
    }
  });

  return <primitive ref={modelRef} object={gltf.scene} />;
}

function TrellisModel3dView({ modelSource }) {
  return (
    <Canvas
      camera={{ position: [3, 3, 3], fov: 50 }}
      style={{ background: "#3A3A3A", width: "100%", height: "100%" }}
    >
      <gridHelper args={[10, 10, "#666666", "#444444"]} />
      <ambientLight intensity={4} />
      <directionalLight position={[5, 10, 7.5]} intensity={1} castShadow />
      <spotLight
        position={[10, 15, 10]}
        angle={0.3}
        penumbra={1}
        intensity={1.2}
        castShadow
      />
      <OrbitControls />
      <Suspense
        fallback={
          <Html center>
            <div
              style={{
                color: "#A0A3A6",
                fontSize: "13px",
                whiteSpace: "nowrap",
              }}
            >
              Loading model...
            </div>
          </Html>
        }
      >
        <TrellisModelMesh modelSource={modelSource} />
      </Suspense>
    </Canvas>
  );
}

const ModelViewer = ({ setOpen, models, onReload }) => {
  const {
    state: {
      selected_index,
      selected_model,
      selected_table,
      selected_task_type,
      editorOpen,
      models: contextModels,
    },
    setState,
  } = useData(); // Access Context state, including selected_table
  const username = localStorage.getItem("username");
  const userRole = localStorage.getItem("user_role");

  // Get task type from context or localStorage as fallback
  const taskType =
    selected_task_type || localStorage.getItem("current_task_type");

  // Save task type to localStorage when it's available
  useEffect(() => {
    if (selected_task_type) {
      localStorage.setItem("current_task_type", selected_task_type);
    }
  }, [selected_task_type]);

  console.log("Debug - selected_task_type:", selected_task_type);
  console.log("Debug - taskType (with fallback):", taskType);
  console.log("Debug - selected_model:", selected_model);

  // Use context models if available, otherwise fall back to props
  const currentModels =
    contextModels && contextModels.length > 0 ? contextModels : models;

  const [model_source, setModelSource] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [showRejectTextarea, setShowRejectTextarea] = useState(false);
  const [rejectFeedback, setRejectFeedback] = useState("");

  // Function to handle model verification
  const handleVerifyModel = async (modelId, boolType) => {
    if (isVerifying) return;

    setIsVerifying(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL_FLASK}/user/tasks/verify`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          bool_type: boolType,
          model_id: modelId,
          task_type: taskType,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("Model verified successfully:", result);

        // Check if the API returned success message
        if (result.message === "Model verified successfully") {
          if (boolType == true) {
            toast.success("Model verified successfully!");
          } else {
            toast.success("Model un-verified successfully!");
          }

          // Update the model state in context
          setState((prev) => {
            const updatedModels = prev.models.map((model) => {
              const currentModelId = model.uid || model.model_id;
              if (currentModelId === modelId) {
                if (taskType === "classification") {
                  return {
                    ...model,
                    classify_reviewed: boolType,
                    classify_rejected: false,
                    rejection_feedback: boolType
                      ? ""
                      : model.rejection_feedback,
                  };
                } else {
                  return {
                    ...model,
                    model_reviewed: boolType,
                    model_rejected: false,
                    rejection_feedback: boolType
                      ? ""
                      : model.rejection_feedback,
                  };
                }
              }
              return model;
            });

            // Also update selected_model
            let updatedSelectedModel = prev.selected_model;
            if (
              prev.selected_model &&
              (prev.selected_model.uid === modelId ||
                prev.selected_model.model_id === modelId)
            ) {
              if (taskType === "classification") {
                updatedSelectedModel = {
                  ...prev.selected_model,
                  classify_reviewed: boolType,
                  classify_rejected: false,
                  rejection_feedback: boolType
                    ? ""
                    : prev.selected_model.rejection_feedback,
                };
              } else {
                updatedSelectedModel = {
                  ...prev.selected_model,
                  model_reviewed: boolType,
                  model_rejected: false,
                  rejection_feedback: boolType
                    ? ""
                    : prev.selected_model.rejection_feedback,
                };
              }
            }

            return {
              ...prev,
              models: updatedModels,
              selected_model: updatedSelectedModel,
            };
          });
        } else {
          toast.error("An error occurred");
        }
      } else {
        const error = await response.json();
        console.error("Failed to verify model:", error);
        toast.error("An error occurred");
      }
    } catch (error) {
      console.error("Error verifying model:", error);
      toast.error("An error occurred");
    } finally {
      setIsVerifying(false);
    }
  };

  // Function to handle model rejection
  const handleRejectModel = async (modelId, feedback) => {
    if (isVerifying) return;

    setIsVerifying(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${BASE_URL_FLASK}/user/tasks/reject`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model_id: modelId,
          task_type: taskType,
          feedback: feedback,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log("Model rejected successfully:", result);

        toast.success("Model rejected successfully!");

        // Update the model state in context
        setState((prev) => {
          const updatedModels = prev.models.map((model) => {
            const currentModelId = model.uid || model.model_id;
            if (currentModelId === modelId) {
              if (taskType === "classification") {
                return {
                  ...model,
                  classify_rejected: true,
                  classify_reviewed: false,
                  rejection_feedback: feedback,
                };
              } else {
                return {
                  ...model,
                  model_rejected: true,
                  model_reviewed: false,
                  rejection_feedback: feedback,
                };
              }
            }
            return model;
          });

          // Also update selected_model
          let updatedSelectedModel = prev.selected_model;
          if (
            prev.selected_model &&
            (prev.selected_model.uid === modelId ||
              prev.selected_model.model_id === modelId)
          ) {
            if (taskType === "classification") {
              updatedSelectedModel = {
                ...prev.selected_model,
                classify_rejected: true,
                classify_reviewed: false,
                rejection_feedback: feedback,
              };
            } else {
              updatedSelectedModel = {
                ...prev.selected_model,
                model_rejected: true,
                model_reviewed: false,
                rejection_feedback: feedback,
              };
            }
          }

          return {
            ...prev,
            models: updatedModels,
            selected_model: updatedSelectedModel,
          };
        });

        // Reset textarea
        setShowRejectTextarea(false);
        setRejectFeedback("");
      } else {
        const error = await response.json();
        console.error("Failed to reject model:", error);
        toast.error("Failed to reject model");
      }
    } catch (error) {
      console.error("Error rejecting model:", error);
      toast.error("An error occurred");
    } finally {
      setIsVerifying(false);
    }
  };

  const submitRejection = () => {
    if (!rejectFeedback.trim()) {
      toast.error("Please provide feedback for rejection");
      return;
    }
    const modelId = selected_model?.uid || selected_model?.model_id;
    handleRejectModel(modelId, rejectFeedback);
  };

  // Function to handle closing the modal
  const handleClose = () => {
    setOpen((prev) => !prev); // Close the modal
    setState((prev) => ({
      ...prev,
      selected_index: null, // Reset selected_index in Context
      glb_model: "",
      editorOpen: false, // Close the editor
    }));
  };

  // Remove the useEffect that automatically closes the dialog
  // The 3D editor should only close when the user explicitly closes it

  // Function to get the status text and color
  const getStatusText = (status) => {
    switch (status) {
      case 0:
        return { text: "N/A", color: "#A0A3A6" }; // Gray for N/A
      case 1:
        return { text: "High Quality", color: "#00FF00" }; // Green for Keep
      case 2:
        return { text: "Partial", color: "#FF9900" }; // Yellow for Photogrammetry
      case 3:
        return { text: "Don't Keep", color: "#FF0000" }; // Red for Don't Keep
      case 4:
        return { text: "Environment", color: "#800080" }; // Purple for Manual Review
      case 5:
        return { text: "Re Texture", color: "#ffffff" }; // white for Re Texture
      default:
        return { text: "Unknown", color: "#A0A3A6" }; // Default gray for unknown
    }
  };

  const { text, color } = getStatusText(selected_model?.status);

  // Function to update the status of the current model
  const updateStatus = async (status) => {
    try {
      const modelId = selected_model?.uid || selected_model?.model_id;
      const payload = {
        uid: modelId, // Pass the model UID/ID
        status: status, // Pass the new status
        table_name: selected_table, // Include the selected table name
        username: username,
      };

      const res = await UpdateStatus(payload); // Call UpdateStatus service

      if (res.hasError) {
        return console.error("Failed to update the status");
      }

      // Update models in state
      setState((prev) => {
        // Update the status in the main models array
        const updatedModels = prev.models.map((item) => {
          const itemId = item.uid || item.model_id;
          return itemId === modelId ? { ...item, status: status } : item;
        });

        // Filter models by current status filter
        const filteredModels = updatedModels.filter(
          (model) => model.status === prev.selected_status,
        );

        // Find the index of the current model in the filtered list
        const currentFilteredIndex = filteredModels.findIndex((model) => {
          const id = model.uid || model.model_id;
          return id === modelId;
        });

        // Move to next filtered model, or close viewer if at end
        if (currentFilteredIndex < filteredModels.length - 1) {
          const nextModel = filteredModels[currentFilteredIndex + 1];
          const nextModelId = nextModel.uid || nextModel.model_id;
          // Find index in updatedModels
          const nextIndex = updatedModels.findIndex((model) => {
            const id = model.uid || model.model_id;
            return id === nextModelId;
          });
          return {
            ...prev,
            models: updatedModels,
            selected_index: nextIndex,
            selected_model: updatedModels[nextIndex],
          };
        } else {
          // If no next model, close viewer
          setOpen(false);
          return {
            ...prev,
            models: updatedModels,
            selected_index: null,
            selected_model: null,
          };
        }
      });
    } catch (error) {
      console.error("Error while updating status:", error);
      // Optionally handle the error (e.g., show a toast notification)
    }
  };

  // Calculate current position in filtered models
  const filteredModels = models; // This is the filtered list passed as prop
  const currentModelId = selected_model?.uid || selected_model?.model_id;
  const currentFilteredIndex = filteredModels.findIndex((model) => {
    const modelId = model.uid || model.model_id;
    return modelId === currentModelId;
  });

  // Navigation Handlers
  // Navigation Handlers
  const handleNext = () => {
    if (currentFilteredIndex < filteredModels.length - 1) {
      const nextModel = filteredModels[currentFilteredIndex + 1];
      const nextModelId = nextModel.uid || nextModel.model_id;

      // Find the index in the full models array (for context)
      const fullModels =
        contextModels && contextModels.length > 0 ? contextModels : models;
      const nextFullIndex = fullModels.findIndex((model) => {
        const modelId = model.uid || model.model_id;
        return modelId === nextModelId;
      });

      setState((prev) => ({
        ...prev,
        selected_index: nextFullIndex,
        selected_model: nextModel,
      }));
    }
  };

  const handlePrevious = () => {
    if (currentFilteredIndex > 0) {
      const previousModel = filteredModels[currentFilteredIndex - 1];
      const previousModelId = previousModel.uid || previousModel.model_id;

      // Find the index in the full models array (for context)
      const fullModels =
        contextModels && contextModels.length > 0 ? contextModels : models;
      const previousFullIndex = fullModels.findIndex((model) => {
        const modelId = model.uid || model.model_id;
        return modelId === previousModelId;
      });

      setState((prev) => ({
        ...prev,
        selected_index: previousFullIndex,
        selected_model: previousModel,
      }));
    }
  };

  useEffect(() => {
    if (!selected_model) return; // Exit if no model is selected

    let model_path;
    const modelId = selected_model.uid || selected_model.model_id;

    // Handle different model path structures
    if (selected_model.model_path) {
      // New data structure has model_path directly
      model_path = selected_model.model_path;
    } else if (modelId && modelId.includes("sketchfab.com")) {
      // Check if the model is from Sketchfab (old structure)
      model_path = `${modelId.split("https://sketchfab.com/3d-")[1]}.glb`;
    } else {
      // Default path structure
      model_path = `models/${modelId}.glb`;
    }

    async function getSource() {
      try {
        const response = await fetch(
          `${BASE_URL_FLASK}/${model_path}?aligned=${selected_model?.aligned}`,
        );
        const res = await response.text();
        setModelSource(res);
      } catch (error) {
        console.error("Error fetching url:", error);
      }
    }

    getSource();
  }, [selected_model]);

  const OpenThreeEditor = async () => {
    if (!model_source) return;
    openDialog();
  };

  let [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTrellisViewerOpen, setIsTrellisViewerOpen] = useState(false);

  function openDialog() {
    setIsDialogOpen(true);
    setState((prev) => ({
      ...prev,
      editorOpen: true, // Open the editor
    }));
  }

  function closeDialog() {
    setIsDialogOpen(false);
    setState((prev) => ({
      ...prev,
      editorOpen: false, // Close the editor
    }));

    // Always reload the MainModels page data when ThreeDEditor closes
    // This ensures the page reflects any changes made in the 3D editor
    if (onReload) {
      handleClose();
      onReload();
    }
  }

  // Download handler for .glb model using model_source (signed URL)
  const handleDownload = () => {
    if (!model_source) return;
    const modelId = selected_model?.uid || selected_model?.model_id || "model";
    const link = document.createElement("a");
    link.href = model_source;
    link.download = `${modelId}.glb`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Dialog open={true} onClose={handleClose} className="relative z-10 ">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-black bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className={`relative h-[53rem] p-1 sm:w-[105rem] transform overflow-auto scrollbar-color rounded-[25px] bg-[#06090B] text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-2 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95`}
              style={{
                borderColor: selected_model?.aligned ? "#00FF00" : "#A0A3A6 ",
                borderWidth: "1px",
              }}
            >
              {/* Close Button */}
              <div className="flex justify-end">
                <button
                  onClick={handleClose}
                  className="text-white text-2xl font-bold hover:text-gray-400"
                  aria-label="Close Modal"
                >
                  &times;
                </button>
              </div>

              {/* Model Details */}
              <div className="flex flex-col items-center p-2 mt-2">
                <div className="flex flex-row gap-2 items-center">
                  <span className="text-[18px] font-semibold text-[#A0A3A6]">
                    Model ID :
                  </span>
                  <span className="text-[18px] font-semibold text-[#0E3F71]">
                    {selected_model?.uid || selected_model?.model_id}
                  </span>
                  {/* Verification checkbox - only for Reviewer and Admin */}
                  {(userRole === "Reviewer" || userRole === "Admin") && (
                    <div className="ml-4 flex items-center gap-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="verify-checkbox"
                          className="mr-2 w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                          disabled={isVerifying}
                          checked={
                            taskType === "classification"
                              ? selected_model?.classify_reviewed || false
                              : selected_model?.model_reviewed || false
                          }
                          onChange={(e) => {
                            const modelId =
                              selected_model?.uid || selected_model?.model_id;
                            const boolType = e.target.checked;
                            handleVerifyModel(modelId, boolType);
                          }}
                        />
                        <label
                          htmlFor="verify-checkbox"
                          className={`text-sm ${
                            isVerifying ? "text-gray-400" : "text-green-400"
                          } cursor-pointer`}
                        >
                          {isVerifying ? "Verifying..." : "Verify Model"}
                        </label>
                      </div>
                      {/* Only show reject button for alignment tasks */}
                      {taskType === "alignment" && (
                        <button
                          onClick={() =>
                            setShowRejectTextarea(!showRejectTextarea)
                          }
                          disabled={isVerifying}
                          className="text-sm text-red-400 hover:text-red-300 disabled:text-gray-400 cursor-pointer bg-red-900/50 px-3 py-1 rounded"
                        >
                          {showRejectTextarea ? "Cancel" : "Reject"}
                        </button>
                      )}
                    </div>
                  )}

                  <a
                    href={selected_model?.uid || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-1 text-[#A0A3A6]"
                    style={{
                      display:
                        selected_model?.uid &&
                        selected_model.uid.includes("http")
                          ? "inline"
                          : "none",
                    }}
                  >
                    <FiExternalLink size={24} />
                  </a>
                  {/* Download Button */}
                  <button
                    onClick={handleDownload}
                    className="ml-4 px-3 py-1 rounded bg-[#0E3F71] text-white text-[15px] hover:bg-[#125E12] transition"
                    disabled={!model_source}
                    title={
                      !model_source ? "Model not loaded yet" : "Download .glb"
                    }
                  >
                    Download .glb
                  </button>
                </div>

                {/* Display rejection status and feedback */}
                {((taskType === "classification" &&
                  selected_model?.classify_rejected) ||
                  (taskType === "alignment" &&
                    selected_model?.model_rejected)) && (
                  <div className="mt-4 bg-red-900/50 border border-red-500 text-white px-4 py-3 rounded-lg max-w-2xl">
                    <p className="font-bold text-red-400 mb-1">
                      ❌ Model Rejected
                    </p>
                    {selected_model?.rejection_feedback && (
                      <p className="text-sm text-gray-200">
                        <span className="font-semibold">Feedback:</span>{" "}
                        {selected_model.rejection_feedback}
                      </p>
                    )}
                  </div>
                )}

                {/* Show rejection textarea for alignment tasks */}
                {showRejectTextarea &&
                  taskType === "alignment" &&
                  (userRole === "Reviewer" || userRole === "Admin") && (
                    <div className="mt-4 max-w-2xl">
                      <label className="block text-[#FF9900] text-sm font-semibold mb-2">
                        Rejection Feedback:
                      </label>
                      <textarea
                        className="w-full h-24 px-4 py-2 bg-[#0A1219] text-white border border-[#FF9900] rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF9900] mb-2"
                        placeholder="Enter your feedback for rejection..."
                        value={rejectFeedback}
                        onChange={(e) => setRejectFeedback(e.target.value)}
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={submitRejection}
                          disabled={isVerifying || !rejectFeedback.trim()}
                          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed"
                        >
                          {isVerifying ? "Rejecting..." : "Submit Rejection"}
                        </button>
                        <button
                          onClick={() => {
                            setShowRejectTextarea(false);
                            setRejectFeedback("");
                          }}
                          disabled={isVerifying}
                          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                <div className="flex flex-row gap-2">
                  <span className="text-[18px] font-semibold text-[#A0A3A6]">
                    Status :
                  </span>
                  <span
                    className="text-[18px] font-semibold"
                    style={{ color: color }}
                  >
                    {text}
                  </span>
                </div>
              </div>

              {/* Model Images */}
              <div
                key={selected_model?.thumbnail_path}
                className="px-[12rem] py-6 flex justify-center gap-12 flex-wrap overflow-auto"
              >
                {Array.from({ length: 5 }, (_, i) => {
                  let url;

                  if (selected_model?.thumbnail_path?.startsWith("previews/")) {
                    const basePath =
                      selected_model.thumbnail_path.split("-")[0]; // removes any -N suffix
                    url = i === 0 ? `${basePath}.jpg` : `${basePath}-${i}.jpg`;
                  } else {
                    const basePath =
                      selected_model?.thumbnail_path?.split("-")[0];
                    url = i === 0 ? `${basePath}.jpg` : `${basePath}-${i}.jpg`;
                  }

                  return url;
                }).map((url, index) => (
                  <div
                    key={index}
                    className="w-[275px] h-[275px] border border-[#A0A3A6] hover:border-[#FF9900] cursor-pointer rounded-lg bg-black transform transition-transform duration-300 hover:scale-110"
                  >
                    <a
                      href={`${BASE_URL_FLASK}/${url}${
                        selected_model?.preview_updated_at
                          ? `?t=${selected_model.preview_updated_at}`
                          : ""
                      }`}
                      target="_blank"
                    >
                      <img
                        src={`${BASE_URL_FLASK}/${url}${
                          selected_model?.preview_updated_at
                            ? `?t=${selected_model.preview_updated_at}`
                            : ""
                        }`}
                        alt={`2D Image Preview ${index + 1}`}
                        className="w-full h-full object-contain rounded-lg"
                        loading="lazy"
                      />
                    </a>
                  </div>
                ))}

                {/* Trellis 3D model viewer button — only for classification tasks */}
                {taskType === "classification" && model_source && (
                  <div
                    onClick={() => setIsTrellisViewerOpen(true)}
                    className="w-[275px] h-[275px] border border-[#A0A3A6] hover:border-[#FF9900] cursor-pointer rounded-lg bg-black transform transition-transform duration-300 hover:scale-110 flex flex-col items-center justify-center gap-2"
                  >
                    <span className="text-[#FF9900] font-bold text-lg">3D MODEL</span>
                    <span className="text-[#A0A3A6] text-sm text-center px-4">Click to view in 3D</span>
                  </div>
                )}

                {/* Only show Sketchfab iframe for classification tasks */}
                {taskType !== "alignment" && (
                  <div className="sketchfab-embed-wrapper w-[275px] h-[275px] border border-[#A0A3A6] hover:border-[#FF9900] cursor-pointer rounded-lg bg-black transform transition-transform duration-300 hover:scale-110">
                    <iframe
                      frameBorder="0"
                      // allowfullscreen=""
                      mozallowfullscreen="true"
                      webkitallowfullscreen="true"
                      width={"273"}
                      height={"273"}
                      allow="autoplay; fullscreen; xr-spatial-tracking"
                      xr-spatial-tracking=""
                      execution-while-out-of-viewport=""
                      execution-while-not-rendered=""
                      web-share=""
                      src={selected_model?.model_id + "/embed"}
                    >
                      {" "}
                    </iframe>
                  </div>
                )}

                {["1", "4", "5"].includes(String(selected_model?.status)) &&
                !(userRole === "Annotator" && taskType === "classification") &&
                taskType !== "classification" ? (
                  <>
                    {/* Main 3D Editor Screenshot */}
                    <div
                      onClick={OpenThreeEditor}
                      className="w-[275px] h-[275px] border border-[#A0A3A6] text-[#FF9900] text-[20px] flex items-center justify-center hover:border-[#FF9900] cursor-pointer contain-content rounded-lg bg-black transform transition-transform duration-300 hover:scale-110"
                    >
                      {selected_model?.thumbnail_path ? (
                        <img
                          src={`${BASE_URL_FLASK}/${
                            selected_model?.thumbnail_path.split("-")[0]
                          }.jpg?screenshot=true${
                            selected_model?.preview_updated_at
                              ? `&t=${selected_model.preview_updated_at}`
                              : ""
                          }`}
                          alt={`3D Editor Screenshot`}
                          className="w-full h-full object-contain rounded-lg"
                          loading="lazy"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.parentElement.innerHTML =
                              '<div class="flex flex-col items-center justify-center h-full"><span class="text-[#FF9900] font-bold text-lg">3D EDITOR</span><span class="text-[#A0A3A6] text-sm mt-2 text-center px-4">Screenshot will be shown here after editing in 3D Editor</span></div>';
                          }}
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full">
                          <span className="text-[#FF9900] font-bold text-lg">
                            3D EDITOR
                          </span>
                          <span className="text-[#A0A3A6] text-sm mt-2 text-center px-4">
                            Screenshot will be shown here after editing in 3D
                            Editor
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Additional Screenshot -5 */}
                    <div className="w-[275px] h-[275px] border border-[#A0A3A6] hover:border-[#FF9900] cursor-pointer rounded-lg bg-black transform transition-transform duration-300 hover:scale-110">
                      {selected_model?.thumbnail_path ? (
                        <img
                          src={`${BASE_URL_FLASK}/${
                            selected_model?.thumbnail_path.split("-")[0]
                          }-5.jpg?screenshot=true${
                            selected_model?.preview_updated_at
                              ? `&t=${selected_model.preview_updated_at}`
                              : ""
                          }`}
                          alt={`3D Editor Screenshot -5`}
                          className="w-full h-full object-contain rounded-lg"
                          loading="lazy"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.parentElement.innerHTML =
                              '<div class="flex flex-col items-center justify-center h-full"><span class="text-[#A0A3A6] text-sm mt-2 text-center px-4">Screenshot -5 will be shown here after editing in 3D Editor</span></div>';
                          }}
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full">
                          <span className="text-[#A0A3A6] text-sm mt-2 text-center px-4">
                            Screenshot 2 will be shown here after editing in 3D
                            Editor
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Additional Screenshot -6 */}
                    <div className="w-[275px] h-[275px] border border-[#A0A3A6] hover:border-[#FF9900] cursor-pointer rounded-lg bg-black transform transition-transform duration-300 hover:scale-110">
                      {selected_model?.thumbnail_path ? (
                        <img
                          src={`${BASE_URL_FLASK}/${
                            selected_model?.thumbnail_path.split("-")[0]
                          }-6.jpg?screenshot=true${
                            selected_model?.preview_updated_at
                              ? `&t=${selected_model.preview_updated_at}`
                              : ""
                          }`}
                          alt={`3D Editor Screenshot -6`}
                          className="w-full h-full object-contain rounded-lg"
                          loading="lazy"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.parentElement.innerHTML =
                              '<div class="flex flex-col items-center justify-center h-full"><span class="text-[#A0A3A6] text-sm mt-2 text-center px-4">Screenshot -6 will be shown here after editing in 3D Editor</span></div>';
                          }}
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full">
                          <span className="text-[#A0A3A6] text-sm mt-2 text-center px-4">
                            Screenshot 3 will be shown here after editing in 3D
                            Editor
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div></div>
                )}
              </div>
              {/* Action Buttons */}
              <div className="flex sm:flex-row flex-col items-center sm:gap-0 gap-4 justify-between p-4 ml-8 mr-8">
                {/* Previous Button */}
                <div>
                  <button
                    onClick={handlePrevious}
                    disabled={currentFilteredIndex <= 0}
                    className={`h-[40px] sm:w-[165px] w-[180px] rounded-[10px] bg-[#15232B] text-[15px] text-[#A0A3A6] ${
                      currentFilteredIndex <= 0
                        ? "opacity-50"
                        : "hover:bg-[#0b1317]"
                    }`}
                  >
                    {"< Previous Model"}
                  </button>
                </div>

                {/* Status Update Buttons - Hidden for annotators with alignment tasks */}
                {!(userRole === "Annotator" && taskType === "alignment") && (
                  <div className="flex sm:gap-6 gap-2">
                    <button
                      onClick={() => updateStatus(1)} // Keep Model
                      className="h-[40px] sm:w-[165px] w-[110px] rounded-[10px] bg-[#125E12] text-[15px] text-[#06090B] hover:bg-[#092909]"
                    >
                      High Quality
                    </button>
                    <button
                      onClick={() => updateStatus(2)} // Photogrammetry
                      className="h-[40px] sm:w-[165px] w-[130px] rounded-[10px] bg-[#b59e22] text-[15px] text-[#06090B] hover:bg-[#594e15]"
                    >
                      Partial
                    </button>
                    <button
                      onClick={() => updateStatus(5)} // Re Texture
                      className="h-[40px] sm:w-[165px] w-[110px] rounded-[10px] bg-white text-[15px] text-[#06090B] hover:bg-[#545759]"
                    >
                      Re Texture
                    </button>
                    <button
                      onClick={() => updateStatus(3)} // Discard Model
                      className="h-[40px] sm:w-[165px] w-[110px] rounded-[10px] bg-[#6F1F1F] text-[15px] text-[#A0A3A6] hover:bg-[#360f0f]"
                    >
                      Don't Keep
                    </button>
                    <button
                      onClick={() => updateStatus(4)} // Manual Review
                      className="h-[40px] sm:w-[165px] w-[110px] rounded-[10px] bg-[#A0A3A6] text-[15px] text-[#06090B] hover:bg-[#545759]"
                    >
                      Environment
                    </button>
                  </div>
                )}

                {/* Next Button */}
                <div>
                  <button
                    onClick={handleNext}
                    disabled={currentFilteredIndex >= filteredModels.length - 1}
                    className={`h-[40px] sm:w-[165px] w-[180px] rounded-[10px] bg-[#15232B] text-[15px] text-[#A0A3A6] ${
                      currentFilteredIndex >= filteredModels.length - 1
                        ? "opacity-50"
                        : "hover:bg-[#0b1317]"
                    }`}
                  >
                    {"Next Model >"}
                  </button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      {isDialogOpen && (
        <Dialog
          open={isDialogOpen}
          onClose={() => {}}
          className="relative z-10"
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-black bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
          />
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <DialogPanel
                transition
                className="relative h-[90vh] sm:w-[96vw] border-[0px] border-[#A0A3A6] transform  scrollbar-color rounded-[25px] bg-[#06090B] text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95 overflow-hidden"
              >
                <ThreeDEditor close={closeDialog} model_source={model_source} />
              </DialogPanel>
            </div>
          </div>
        </Dialog>
      )}
      {isTrellisViewerOpen && (
        <Dialog
          open={isTrellisViewerOpen}
          onClose={() => setIsTrellisViewerOpen(false)}
          className="relative z-20"
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-black bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
          />
          <div className="fixed inset-0 z-20 w-screen overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 sm:p-0">
              <DialogPanel
                transition
                className="relative h-[90vh] sm:w-[96vw] rounded-[25px] bg-[#06090B] shadow-xl overflow-hidden transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
              >
                <button
                  onClick={() => setIsTrellisViewerOpen(false)}
                  className="absolute top-3 right-4 z-10 text-white text-2xl font-bold hover:text-gray-400"
                  aria-label="Close 3D Viewer"
                >
                  &times;
                </button>
                <TrellisModel3dView modelSource={model_source} />
              </DialogPanel>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
};

export default ModelViewer;
